import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generarPaginasRecepcion } from '../pages/Recepcion/reportes/exportacionPdf';

const PALETA_BN_COMPATIBLE = {
    gris0: [242, 242, 242],
    gris1: [200, 200, 200],
    gris2: [150, 150, 150],
    gris3: [100, 100, 100],
    gris4: [50, 50, 50],
    azul: [0, 51, 102],
    verde: [51, 102, 51],
    naranja: [204, 102, 0],
    rojo: [153, 0, 0],
    teal: [0, 102, 102],
};

export const COLORES_GRAFICOS_BN = [
    '#004080', '#336633', '#CC6600', '#990000', '#663399',
    '#664433', '#8B2345', '#006666', '#333333', '#660066',
];

export const generarPDFReporte = (config) => {
    const { tipo = 'RTI' } = config;

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const m = 14;
    let currentY = 20;

    const config_tipo = {
        ART: { titulo: 'Reporte de Trámites ART', colorPrimario: PALETA_BN_COMPATIBLE.naranja },
        RTI: { titulo: 'Reporte de Trámites RTI', colorPrimario: PALETA_BN_COMPATIBLE.verde },
        RECEPCION: { titulo: 'Reporte de Recepción', colorPrimario: PALETA_BN_COMPATIBLE.azul }
    };
    const conf = config_tipo[tipo] || config_tipo.RTI;

    /**
     * Dibuja un gráfico ocupando la mitad de la página aproximadamente
     */
    const insertarGraficoEnPagina = (chartRef, titulo, yPos) => {
        // El ref puede existir pero apuntar a un componente que no está montado (ej. otra
        // pestaña activa al momento de exportar, los gráficos de esa pestaña nunca se
        // renderizaron) — igual hay que imprimir el título, si no la página queda sin
        // encabezado y la tabla que sigue arranca pegada al borde.
        if (!chartRef.current || !chartRef.current.canvas) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...conf.colorPrimario);
            doc.text(titulo.toUpperCase(), m, yPos);
            return yPos + 8;
        }

        const canvas = chartRef.current.canvas;
        const scale = 2; // Alta resolución para evitar pixelado
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width * scale;
        tempCanvas.height = canvas.height * scale;
        const ctx = tempCanvas.getContext('2d');
        ctx.scale(scale, scale);
        ctx.drawImage(canvas, 0, 0);
        const imgData = tempCanvas.toDataURL('image/png', 1.0);

        // Si el gráfico supera el límite inferior, nueva página
        const alturaDisponible = pageH - yPos - 20;
        const alturaEstimada = 85; // Altura fija máxima para 2 por página

        if (alturaDisponible < alturaEstimada) {
            doc.addPage();
            yPos = 20;
        }

        // Título del gráfico
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...conf.colorPrimario);
        doc.text(titulo.toUpperCase(), m, yPos);

        // Ajuste de dimensiones
        const ratio = canvas.height / canvas.width;
        const anchoGrafico = pageW - (m * 2);
        let altoGrafico = anchoGrafico * ratio;

        // Limitar altura para asegurar que entren 2 por página
        if (altoGrafico > 80) altoGrafico = 80;

        doc.addImage(imgData, 'PNG', m, yPos + 5, anchoGrafico, altoGrafico);

        return yPos + altoGrafico + 25; // Espaciado para el siguiente
    };

    // --- PÁGINA 1: ENCABEZADO Y RESUMEN ---
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 0, pageW, 40, 'F');
    doc.setFontSize(20);
    doc.setTextColor(...conf.colorPrimario);
    doc.text(conf.titulo, pageW / 2, 20, { align: 'center' });

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generado el ${new Date().toLocaleDateString('es-AR')} ${new Date().toLocaleTimeString('es-AR')}`, pageW / 2, 28, { align: 'center' });

    // Recepción arma sus páginas desde los datos (gráficos dibujados fuera de pantalla), no
    // desde refs de la pantalla — ver pages/Recepcion/reportes/exportacionPdf.js.
    if (tipo === 'RECEPCION') {
        return generarPaginasRecepcion(doc, config, conf, { pageW, pageH, m });
    }

    const { resumen = {}, porEstado = [], tiempoPromedio = [] } = config;
    const referencias = config.referencias || {};

    currentY = 50;
    autoTable(doc, {
        head: [['Métrica de Resumen', 'Valor']],
        body: [
            ['Total de Trámites', resumen.totalTramites || '0'],
            ['Estados Activos', resumen.estadosActivos || '0'],
            ['Estado Predominante', resumen.estadoMasFrecuenteNombre || '—']
        ],
        startY: currentY,
        headStyles: { fillColor: conf.colorPrimario },
        margin: { left: m, right: m }
    });

    currentY = doc.lastAutoTable.finalY + 10;

    // Tabla simplificada en la primera página
    autoTable(doc, {
        head: [['Distribución por Estado', 'Cantidad', 'Porcentaje']],
        body: porEstado.slice(0, 6).map(e => [e.nombreEstado, e.cantidad, `${e.porcentaje}%`]),
        startY: currentY,
        headStyles: { fillColor: [80, 80, 80] },
        styles: { fontSize: 8 },
        margin: { left: m, right: m }
    });

    // --- PÁGINA 2: GRÁFICOS (2 POR PÁGINA) ---
    doc.addPage();
    currentY = 20;

    if (referencias.pieRef) {
        currentY = insertarGraficoEnPagina(referencias.pieRef, "1. Proporción General", currentY);
    }

    if (referencias.barRef) {
        currentY = insertarGraficoEnPagina(referencias.barRef, "2. Volúmenes por Estado", currentY);
    }

    // El tercer gráfico irá en una nueva página o debajo del segundo según espacio
    if (referencias.barTiempoRef) {
        currentY = insertarGraficoEnPagina(referencias.barTiempoRef, "3. Análisis de Tiempos Promedio", currentY);
    }

    // --- PÁGINA FINAL: TABLA DETALLADA ---
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(40);
    doc.text('ANEXO: TABLA DE DATOS COMPLETA', m, 20);

    autoTable(doc, {
        head: [['Estado', 'Cant.', '%', 'Días Prom.', 'Clasificación']],
        body: porEstado.map(e => {
            const t = tiempoPromedio.find(tp => tp.nombreEstado === e.nombreEstado);
            return [e.nombreEstado, e.cantidad, `${e.porcentaje}%`, t?.diasPromedio || '—', t?.clasificacion || '—'];
        }),
        startY: 25,
        headStyles: { fillColor: conf.colorPrimario },
        styles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [250, 250, 250] }
    });

    return doc;
};

export const descargarPDF = (doc, nombreArchivo) => {
    doc.save(`${nombreArchivo}_${new Date().toISOString().split('T')[0]}.pdf`);
};

export const usePDFReportes = () => {
    const generarYDescargar = (config) => {
        const doc = generarPDFReporte(config);
        const nombres = { ART: 'reporte_art', RTI: 'reporte_rti', RECEPCION: 'reporte_recepcion' };
        descargarPDF(doc, nombres[config.tipo] || 'reporte_rti');
    };
    return { generarYDescargar, COLORES_GRAFICOS_BN };
};

const ReportesPDFUtils = {
    generarPDFReporte,
    descargarPDF,
    usePDFReportes,
    COLORES_GRAFICOS_BN,
    PALETA_BN_COMPATIBLE
};

export default ReportesPDFUtils;
